import React from 'react';
import logo_monere from '../../assets/images/logo_monere.png';

/**
 * Términos del servicio y política de privacidad.
 *
 * VERSIÓN PRELIMINAR: redactada a partir de cómo funciona la plataforma y de la
 * Ley 19.628 modificada por la Ley 21.719. Debe revisarla un abogado y completar
 * los datos entre corchetes antes de considerarla definitiva.
 */

interface Section {
    title: string;
    paragraphs: string[];
}

const UPDATED_AT = '13 de octubre de 2026';

const TERMS: Section[] = [
    {
        title: '1. Quiénes somos',
        paragraphs: [
            'Monere ([RAZÓN SOCIAL], RUT [RUT], domicilio [DOMICILIO]) ofrece una plataforma para gestionar el registro de marcas ante INAPI, redactar y firmar contratos, y para que abogados y estudios jurídicos administren a sus clientes.',
            'Estos términos se aplican a toda persona que use la plataforma: clientes, abogados independientes, estudios jurídicos y sus integrantes.'
        ]
    },
    {
        title: '2. Cuentas',
        paragraphs: [
            'Cada persona usa su propia cuenta y es responsable de mantener su contraseña en reserva. Debe avisarnos si sospecha un uso no autorizado.',
            'La información que ingrese debe ser verdadera. Podemos suspender cuentas usadas para fines ilícitos o contrarios a estos términos.'
        ]
    },
    {
        title: '3. Abogados y estudios jurídicos',
        paragraphs: [
            'El abogado independiente o el estudio es responsable de los datos de sus clientes que ingrese a la plataforma, de contar con una base de licitud para tratarlos (por ejemplo, el mandato o el contrato de servicios con su cliente) y de cumplir el secreto profesional.',
            'Respecto de esos datos, Monere actúa como encargado de tratamiento: los trata solo para prestar el servicio y según las instrucciones del estudio, sin usarlos para fines propios ni cederlos (artículo 15 bis de la Ley 19.628). El equipo de Monere no accede a los casos, clientes, contratos, conversaciones con el asistente ni respuestas de checklists del estudio, ni administra las cuentas de sus integrantes y clientes; solo ve datos agregados necesarios para administrar el servicio (plan, cantidad de usuarios, clientes y casos).',
            'El administrador del estudio decide quién integra su equipo y con qué rol. Al retirar a una persona, ella deja de acceder a la información del estudio.'
        ]
    },
    {
        title: '4. Planes, pagos y cancelación',
        paragraphs: [
            'Los planes de pago se cobran por adelantado a través de Flow. Los precios se informan con IVA incluido antes de contratar. Las suscripciones mensuales se renuevan automáticamente hasta que se cancelen.',
            'La cancelación de una suscripción detiene el cobro siguiente: el plan sigue vigente hasta el término del período pagado y luego la cuenta pasa al plan gratuito, sin perder sus datos.',
            'Sin una suscripción vigente se aplican los límites del plan gratuito: se puede seguir trabajando y consultando la información, pero no crecer por sobre esos límites.'
        ]
    },
    {
        title: '5. Servicios de registro de marcas y contratos',
        paragraphs: [
            'Los informes de factibilidad, las solicitudes ante INAPI y los contratos los revisa un abogado. La resolución final de INAPI no depende de Monere ni del abogado y no está garantizada.',
            'El asistente de inteligencia artificial entrega orientación general y no reemplaza la asesoría de un abogado.'
        ]
    },
    {
        title: '6. Firma electrónica',
        paragraphs: [
            'Los contratos y poderes se firman con firma electrónica simple. Los enlaces de firma son personales: quien los reciba no debe compartirlos.'
        ]
    },
    {
        title: '7. Responsabilidad',
        paragraphs: [
            'Hacemos nuestro mejor esfuerzo para que la plataforma esté disponible y segura, pero puede haber interrupciones. [LIMITACIÓN DE RESPONSABILIDAD A DEFINIR POR EL ÁREA LEGAL].'
        ]
    },
    {
        title: '8. Cambios y ley aplicable',
        paragraphs: [
            'Podemos actualizar estos términos; avisaremos los cambios relevantes por correo o en la plataforma.',
            'Estos términos se rigen por la ley chilena. [TRIBUNALES COMPETENTES A DEFINIR].'
        ]
    }
];

const PRIVACY: Section[] = [
    {
        title: '1. Responsable de los datos',
        paragraphs: [
            'Para los clientes que contratan directamente con Monere, el responsable de sus datos es [RAZÓN SOCIAL], RUT [RUT]. Contacto para temas de datos personales: [CORREO DE PRIVACIDAD].',
            'Para los clientes de un abogado independiente o estudio que usa Monere, el responsable es ese abogado o estudio; Monere es su encargado de tratamiento y solo trata los datos por su cuenta. Las solicitudes sobre esos datos pueden dirigirse al estudio o a Monere, que las derivará.'
        ]
    },
    {
        title: '2. Qué datos tratamos',
        paragraphs: [
            'Datos de identificación y contacto (nombre, RUT, correo, teléfono, domicilio), datos de las marcas y solicitudes ante INAPI, contenido de los contratos y sus firmas, poderes firmados, documentos que se suban a la plataforma, mensajes con el asistente de inteligencia artificial y datos de pago (los datos de la tarjeta los recibe Flow, no Monere).'
        ]
    },
    {
        title: '3. Para qué los usamos',
        paragraphs: [
            'Para prestar los servicios contratados (registro de marcas, contratos, firma, gestión de clientes del estudio), cobrar los planes, enviar los correos propios del servicio y cumplir obligaciones legales. No vendemos datos ni los usamos para publicidad de terceros.',
            'Base de licitud: la ejecución del contrato con el titular o con el estudio que lo representa, el cumplimiento de obligaciones legales y, cuando corresponda, el consentimiento.'
        ]
    },
    {
        title: '4. Con quién los compartimos',
        paragraphs: [
            'Solo con proveedores que nos ayudan a prestar el servicio y que actúan como encargados: DigitalOcean (servidores y almacenamiento de archivos, Estados Unidos), Brevo (envío de correos, Unión Europea), Flow (pagos, Chile) y OpenAI (Estados Unidos) para las funciones de inteligencia artificial: el asistente legal (lo que el usuario escriba o suba), el análisis de las respuestas de los checklists, el informe de factibilidad de una marca (nombre y descripción de la marca) y el análisis de documentos que el usuario adjunte. En las páginas públicas usamos Google Analytics y Google Tag Manager (Estados Unidos) para medir visitas; no se usan en la intranet ni en los enlaces de activación, firma o invitación. Exigimos a estos proveedores un nivel adecuado de protección.',
            'Con INAPI y el Diario Oficial cuando se tramita una marca, porque así lo exige el procedimiento.'
        ]
    },
    {
        title: '5. Cuánto tiempo los guardamos',
        paragraphs: [
            'Mientras la cuenta esté activa y luego por el plazo necesario para cumplir obligaciones legales y defender derechos. [PLAZOS ESPECÍFICOS A DEFINIR]. Cuando un estudio deja de usar Monere, sus datos se le devuelven o se eliminan según lo convenido.'
        ]
    },
    {
        title: '6. Seguridad',
        paragraphs: [
            'Los documentos legales (poderes, contratos, certificados y adjuntos) se guardan en almacenamiento privado y se entregan con enlaces temporales; el acceso se controla por rol y por organización; las contraseñas se guardan con un hash irreversible y nunca en texto plano. Si ocurre una vulneración que afecte datos personales, la informaremos según exige la ley.'
        ]
    },
    {
        title: '7. Sus derechos',
        paragraphs: [
            'Puede solicitar acceso, rectificación, supresión, oposición, portabilidad y bloqueo de sus datos, y no ser objeto de decisiones basadas únicamente en tratamiento automatizado, escribiendo a [CORREO DE PRIVACIDAD]. Responderemos en los plazos que fija la ley. También puede reclamar ante la Agencia de Protección de Datos Personales.'
        ]
    }
];

/**
 * Contrato de encargo de tratamiento de datos (art. 15 bis Ley 19.628, mod. Ley 21.719).
 * La versión debe coincidir con DPA_VERSION del backend (api/organizations/vars.py).
 */
export const DPA_VERSION = '2026-10';
const DPA_UPDATED_AT = 'octubre de 2026';

const DPA: Section[] = [
    {
        title: '1. Partes',
        paragraphs: [
            'Este contrato lo celebran, por una parte, el abogado independiente o estudio jurídico que crea su cuenta en Monere (en adelante, "el Estudio"), que actúa como responsable de los datos personales de sus clientes; y por la otra, Monere ([RAZÓN SOCIAL], RUT [RUT], domicilio [DOMICILIO]), que actúa como encargado de tratamiento por cuenta del Estudio.',
            'Se acepta al registrar la cuenta o, para cuentas existentes, desde la página del estudio. Lo acepta en nombre del Estudio su administrador.'
        ]
    },
    {
        title: '2. Objeto',
        paragraphs: [
            'El Estudio encarga a Monere el tratamiento de los datos personales que ingrese a la plataforma, con el único propósito de prestarle el servicio contratado: gestión de clientes, registro de marcas ante INAPI, redacción y firma de contratos, y las demás funciones de la plataforma.'
        ]
    },
    {
        title: '3. Duración',
        paragraphs: [
            'El encargo dura mientras esté vigente la suscripción o cuenta del Estudio en Monere.',
            'Cuando el Estudio se da de baja, Monere conserva los datos por 30 días para que pueda descargarlos o revertir la baja. Cumplido ese plazo, los elimina de forma definitiva.'
        ]
    },
    {
        title: '4. Finalidad',
        paragraphs: [
            'Monere trata los datos solo para prestar el servicio al Estudio. No los usa para fines propios, publicidad, perfilamiento ni para entrenar modelos de inteligencia artificial, y no los vende ni los comparte con terceros salvo lo indicado en la cláusula de sub-encargados.'
        ]
    },
    {
        title: '5. Tipos de datos',
        paragraphs: [
            'Datos de identificación (nombre, apellidos), datos de contacto (correo, teléfono, domicilio), RUT, datos de las marcas y de las solicitudes ante INAPI, contenido de los contratos y sus firmas, poderes y los documentos que el Estudio o sus clientes suban a la plataforma.'
        ]
    },
    {
        title: '6. Categorías de titulares',
        paragraphs: [
            'Los clientes del Estudio y sus representantes legales o apoderados, y los integrantes del Estudio (abogados, asistentes y administradores) que usan la plataforma.'
        ]
    },
    {
        title: '7. Obligaciones de Monere como encargado',
        paragraphs: [
            'Tratar los datos solo según las instrucciones documentadas del Estudio, que se entienden dadas por el uso que el Estudio hace de la plataforma y por este contrato.',
            'Exigir confidencialidad a todo su personal y colaboradores que puedan acceder a los datos. El equipo de Monere no accede al contenido de los casos, clientes ni contratos del Estudio, salvo que el propio Estudio lo pida para resolver un problema de soporte.',
            'Aplicar medidas de seguridad técnicas y organizativas adecuadas: almacenamiento privado de documentos con enlaces temporales, control de acceso por rol y por organización, contraseñas guardadas con hash irreversible y comunicaciones cifradas.',
            'No ceder ni comunicar los datos a terceros, salvo a los sub-encargados indicados en este contrato o cuando lo exija la ley o una autoridad competente.'
        ]
    },
    {
        title: '8. Sub-encargados',
        paragraphs: [
            'El Estudio autoriza a Monere a apoyarse en los siguientes proveedores, que tratan datos solo para la función indicada: DigitalOcean (hosting de servidores y almacenamiento de archivos); Brevo (envío de correos); OpenAI (asistencia de inteligencia artificial en la redacción y el análisis de documentos y marcas); y Flow (procesamiento de pagos).',
            'Monere exige a estos proveedores obligaciones de protección equivalentes a las de este contrato y avisará al Estudio antes de incorporar uno nuevo, para que pueda oponerse.'
        ]
    },
    {
        title: '9. Brechas de seguridad',
        paragraphs: [
            'Si Monere detecta una vulneración de seguridad que afecte los datos del Estudio, se la notificará sin dilación indebida, con la información disponible sobre su naturaleza, los datos y titulares afectados y las medidas adoptadas, para que el Estudio pueda cumplir sus propias obligaciones de aviso.'
        ]
    },
    {
        title: '10. Derechos de los titulares',
        paragraphs: [
            'Monere asistirá al Estudio para responder las solicitudes de acceso, rectificación, supresión, oposición, portabilidad y bloqueo que reciba de los titulares. Si una solicitud llega directamente a Monere, la derivará al Estudio sin responderla por su cuenta.'
        ]
    },
    {
        title: '11. Devolución y eliminación',
        paragraphs: [
            'En cualquier momento, y en especial al terminar el servicio, el administrador del Estudio puede descargar todos sus datos en un archivo ZIP desde la página del estudio.',
            'Al término del encargo, y cumplido el plazo de 30 días de la cláusula 3, Monere elimina los datos del Estudio, salvo los que deba conservar por obligación legal, que mantendrá bloqueados solo por el tiempo que la ley exija.'
        ]
    },
    {
        title: '12. Auditoría',
        paragraphs: [
            'Monere pondrá a disposición del Estudio la información razonable que necesite para verificar el cumplimiento de este contrato, y atenderá auditorías o inspecciones razonables, con aviso previo y sin afectar la seguridad ni la confidencialidad de otros clientes.'
        ]
    },
    {
        title: '13. Ley aplicable',
        paragraphs: [
            'Este contrato se rige por la ley chilena, en particular la Ley 19.628 sobre protección de la vida privada, modificada por la Ley 21.719. [TRIBUNALES COMPETENTES A DEFINIR].'
        ]
    }
];

const LegalPage: React.FC<{ title: string; sections: Section[]; updatedAt?: string; subtitle?: string }> = ({ title, sections, updatedAt = UPDATED_AT, subtitle }) => (
    <div style={{ background: '#f5f6f8', minHeight: '100vh', padding: '32px 16px' }}>
        <div className="mx-auto bg-white rounded shadow-sm p-4 p-md-5" style={{ maxWidth: 820 }}>
            <div className="text-center mb-4">
                <img src={logo_monere} alt="Monere" style={{ height: 40 }} />
            </div>
            <div className="alert alert-warning small" role="note">
                Versión preliminar en revisión legal. Los datos entre corchetes se completarán en la versión definitiva.
            </div>
            <h2 className="mb-1">{title}</h2>
            {subtitle && <p className="text-muted mb-1">{subtitle}</p>}
            <p className="text-muted small mb-4">Última actualización: {updatedAt}</p>
            {sections.map((section) => (
                <section key={section.title} className="mb-4">
                    <h5>{section.title}</h5>
                    {section.paragraphs.map((paragraph, index) => (
                        <p key={index} className="mb-2" style={{ lineHeight: 1.6 }}>{paragraph}</p>
                    ))}
                </section>
            ))}
        </div>
    </div>
);

export const TermsPage: React.FC = () => <LegalPage title="Términos del servicio" sections={TERMS} />;
export const PrivacyPage: React.FC = () => <LegalPage title="Política de privacidad" sections={PRIVACY} />;
export const DataProcessingAgreementPage: React.FC = () => (
    <LegalPage
        title="Contrato de encargo de tratamiento de datos"
        subtitle={`Versión ${DPA_VERSION} · Ley 19.628, modificada por la Ley 21.719 (artículo 15 bis)`}
        sections={DPA}
        updatedAt={DPA_UPDATED_AT}
    />
);
